import { Link } from "react-router-dom";
import { ArrowRight, Phone, ShieldCheck, MailOpen } from "lucide-react";
import { GoldButton, GhostButton } from "../components/Buttons";
import { MaskedLines, Reveal } from "../components/Reveal";
import { SectionHeading } from "../components/SectionHeading";
import { AUDIT_PROGRAM, AUDIT_BENEFITS, AUDIT_TERMS, AUDIT_NOTICE_STEPS, AUDIT_NOTICE_INFO, AUDIT_FAQ, CONTACT, IMAGES } from "../data/content";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import Seo from "../components/Seo";

export default function AuditDefense() {
    return (
        <div data-testid="page-audit-defense">
            <Seo
                title="Audit Defense — IRS Notice Assistance | Freedom Tax Filer"
                description="IRS notice? Our Audit Defense Commitment explains the assistance eligible customers receive when the IRS questions a covered return we prepared."
                path="/audit-defense"
            />
            {/* Hero */}
            <section className="relative pt-44 pb-16 bg-[#172337] overflow-hidden">
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(200,164,93,0.10),transparent_60%)]" />
                <div className="relative z-10 mx-auto max-w-7xl px-5 sm:px-8">
                    <Reveal>
                        <p className="eyebrow-dark">{AUDIT_PROGRAM.eyebrow}</p>
                        <div className="gold-rule mt-5" />
                    </Reveal>
                    <h1 className="font-display text-white text-5xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight mt-8 max-w-4xl">
                        <MaskedLines
                            delay={0.2}
                            lines={[
                                "We're Here for You—",
                                <>
                                    Even After <em className="italic text-gold-gradient">You File.</em>
                                </>,
                            ]}
                        />
                    </h1>
                    <Reveal delay={0.5}>
                        <p className="text-white/70 text-lg leading-relaxed mt-8 max-w-3xl">{AUDIT_PROGRAM.intro}</p>
                    </Reveal>
                </div>
            </section>

            {/* Benefits */}
            <section className="pb-8" data-testid="audit-benefits">
                <div className="mx-auto max-w-7xl px-5 sm:px-8">
                    <SectionHeading eyebrow="What's Included" title="Five ways we stay with you." />
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-14">
                        {AUDIT_BENEFITS.map((b, i) => {
                            const Icon = b.icon;
                            return (
                                <Reveal key={b.title} delay={i * 0.07}>
                                    <div className="h-full bg-white border border-[#E7E4DC] shadow-sm p-8 transition-all duration-300 hover:-translate-y-1 hover:border-[#C8A45D]/60 hover:shadow-md" data-testid={`audit-benefit-card-${i}`}>
                                        <div className="h-12 w-12 rounded-lg border border-[#C8A45D]/50 flex items-center justify-center">
                                            <Icon className="h-5 w-5 text-[#8A6D2F]" />
                                        </div>
                                        <h3 className="font-display text-xl text-[#20242B] mt-7">{b.title}</h3>
                                        <p className="text-[#737985] text-sm leading-relaxed mt-3">{b.text}</p>
                                    </div>
                                </Reveal>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Notice visual */}
            <section className="pb-20 lg:pb-24 px-5 sm:px-8" data-testid="audit-image-band">
                <Reveal>
                    <div className="mx-auto max-w-4xl frame-gold">
                        <img src={IMAGES.auditNotice} alt="An official notice letter on a desk" className="w-full h-[280px] sm:h-[380px] object-cover" loading="lazy" decoding="async" data-testid="audit-notice-image" />
                    </div>
                </Reveal>
            </section>

            {/* Highlighted promise */}
            <section className="py-20 lg:py-24 px-5 sm:px-8 bg-[#172337] border-y border-white/10" data-testid="audit-promise">
                <Reveal>
                    <div className="mx-auto max-w-4xl relative border border-[#C8A45D]/45 bg-[#C8A45D]/[0.08] px-8 sm:px-14 py-12 sm:py-16 text-center">
                        <ShieldCheck className="h-10 w-10 text-[#C8A45D] mx-auto" />
                        <p className="font-display italic text-2xl sm:text-3xl text-white leading-snug mt-6">{AUDIT_PROGRAM.promise}</p>
                        <p className="text-[#E4D2A3] text-sm mt-5">For eligible returns prepared by Freedom Tax Filer, subject to the written service agreement.</p>
                    </div>
                </Reveal>
            </section>

            {/* If a notice arrives */}
            <section className="pb-24 lg:pb-32" data-testid="audit-notice-steps">
                <div className="mx-auto max-w-7xl px-5 sm:px-8">
                    <SectionHeading
                        eyebrow="If a Notice Arrives"
                        title="Three steps. Zero panic."
                        sub="An IRS letter is a request for information — not a verdict. Here is exactly what happens when you bring one to us."
                    />
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-10 mt-14">
                        {AUDIT_NOTICE_STEPS.map((s, i) => (
                            <Reveal key={i} delay={i * 0.1}>
                                <div className="relative border-t border-[#E7E4DC] pt-8">
                                    <span className="absolute top-0 left-0 h-[2px] w-12 bg-[#C8A45D]" />
                                    <span className="font-display text-5xl text-[#8A6D2F]">{String(i + 1).padStart(2, "0")}</span>
                                    <p className="text-[#4A4F58] text-sm sm:text-base leading-relaxed mt-4">{s}</p>                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* Understanding your notice */}
            <section className="pb-24 lg:pb-32" data-testid="audit-notice-info">
                <div className="mx-auto max-w-7xl px-5 sm:px-8">
                    <SectionHeading
                        eyebrow="Before You Worry"
                        title={<>Understanding Your <em className="italic text-[#8A6D2F]">IRS Notice</em></>}
                        sub={AUDIT_NOTICE_INFO.intro}
                    />
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 mt-12">
                        {AUDIT_NOTICE_INFO.examples.map((ex, i) => (
                            <Reveal key={ex.title} delay={i * 0.08}>
                                <div className="h-full bg-white border border-[#E7E4DC] rounded-xl p-7 shadow-sm">
                                    <span className="h-10 w-10 rounded-lg border border-[#C8A45D]/50 bg-[#C8A45D]/10 flex items-center justify-center">
                                        <MailOpen className="h-4 w-4 text-[#8A6D2F]" />
                                    </span>
                                    <h3 className="font-display text-lg text-[#20242B] mt-4">{ex.title}</h3>
                                    <p className="text-[#737985] text-sm leading-relaxed mt-2">{ex.text}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                    <Reveal delay={0.1}>
                        <p className="mt-8 text-[#8A6D2F] text-sm font-medium border-l-2 border-[#C8A45D] pl-4 leading-relaxed max-w-3xl" data-testid="audit-notice-note">
                            {AUDIT_NOTICE_INFO.note}
                        </p>
                    </Reveal>
                </div>
            </section>

            {/* Terms */}
            <section className="pb-24 lg:pb-32 bg-[#F3F1EC] border-t border-[#E7E4DC]" data-testid="audit-terms">
                <div className="mx-auto max-w-7xl px-5 sm:px-8">
                    <SectionHeading
                        eyebrow="The Fine Print, Up Front"
                        title="Clear terms before you commit."
                        sub="You will always know what is covered, what is not, and what it costs — before you purchase, never after."
                    />
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-14">
                        {AUDIT_TERMS.map((t, i) => {
                            const Icon = t.icon;
                            return (
                                <Reveal key={t.title} delay={i * 0.07}>
                                    <div className="h-full bg-white border border-[#E7E4DC] shadow-sm p-8" data-testid={`audit-terms-card-${i}`}>
                                        <div className="flex items-center gap-4">
                                            <div className="h-11 w-11 rounded-lg border border-[#C8A45D]/50 flex items-center justify-center shrink-0">
                                                <Icon className="h-5 w-5 text-[#8A6D2F]" />
                                            </div>
                                            <h3 className="font-display text-xl text-[#20242B]">{t.title}</h3>
                                        </div>
                                        <p className="text-[#737985] text-sm leading-relaxed mt-5">{t.text}</p>
                                    </div>
                                </Reveal>
                            );
                        })}
                    </div>
                    <Reveal delay={0.2}>
                        <p className="text-[#737985] text-xs sm:text-sm leading-relaxed mt-8 max-w-3xl" data-testid="audit-disclaimer">
                            This page is a plain-language summary — the written service agreement governs in every case. Ask us
                            for a copy of the agreement before purchasing: it contains the complete, binding terms of this
                            assistance, including everything covered, excluded, and charged. We do not promise that every IRS
                            matter is covered, that every notice qualifies for assistance, or that penalties, audits, or tax
                            liabilities will be eliminated.
                        </p>
                    </Reveal>
                </div>
            </section>

            {/* Audit FAQ */}
            <section className="pb-24 lg:pb-32" data-testid="audit-faq">
                <div className="mx-auto max-w-7xl px-5 sm:px-8">
                    <SectionHeading eyebrow="Common Questions" title="Audit notice questions, answered." />
                    <div className="max-w-3xl mx-auto mt-10">
                        <Accordion type="single" collapsible className="border-t border-[#E7E4DC]">
                            {AUDIT_FAQ.map((f, i) => (
                                <AccordionItem key={i} value={`audit-faq-${i}`} className="border-b border-[#E7E4DC]" data-testid={`audit-faq-item-${i}`}>
                                    <AccordionTrigger className="font-display text-lg text-[#20242B] hover:text-[#8A6D2F] hover:no-underline text-left py-5 px-0">{f.q}</AccordionTrigger>
                                    <AccordionContent className="text-[#737985] leading-relaxed pb-5 px-0">{f.a}</AccordionContent>
                                </AccordionItem>
                            ))}
                        </Accordion>
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="pb-24 lg:pb-32 bg-[#111318]" data-testid="audit-cta">
                <Reveal>
                    <div className="mx-auto max-w-7xl relative border border-[#C8A45D]/40 bg-[#111318] rounded-2xl px-8 sm:px-14 py-16 sm:py-20 overflow-hidden shadow-xl">
                        <div className="absolute top-0 left-0 h-full w-[3px] bg-gradient-to-b from-transparent via-[#C8A45D] to-transparent" />
                        <p className="eyebrow-dark">{AUDIT_PROGRAM.eyebrow}</p>
                        <h2 className="font-display text-3xl sm:text-5xl text-white mt-5 leading-tight max-w-2xl">
                            Need help with an IRS letter? <em className="italic text-gold-gradient">Contact our team.</em>
                        </h2>
                        <div className="flex flex-col sm:flex-row gap-4 mt-10">
                            <GoldButton asChild data-testid="audit-cta-contact">
                                <Link to="/contact">
                                    Contact Our Team <ArrowRight className="h-4 w-4" />
                                </Link>
                            </GoldButton>
                            {CONTACT.phoneHref && (
                                <GhostButton asChild data-testid="audit-cta-call">
                                    <a href={CONTACT.phoneHref}>
                                        <Phone className="h-4 w-4" /> Call Us
                                    </a>
                                </GhostButton>
                            )}
                        </div>
                    </div>
                </Reveal>
            </section>
        </div>
    );
}
